<script setup lang="ts">
import { Activity, Bot, Plug, Undo2, User } from "@lucide/vue";
import { useQueryClient } from "@tanstack/vue-query";
import { refDebounced } from "@vueuse/core";
import { toast } from "vue-sonner";
import type { ChangeSet } from "@/api/contracts";
import { revertChangeSet, useKnowledgeChanges } from "@/api/knowledge";
import EmptyState from "@/components/common/EmptyState.vue";
import ErrorState from "@/components/common/ErrorState.vue";
import FilterBar from "@/components/common/FilterBar.vue";
import SimpleSelect from "@/components/common/SimpleSelect.vue";
import ListSkeleton from "@/components/common/ListSkeleton.vue";
import PageHeader from "@/components/common/PageHeader.vue";
import RelativeTime from "@/components/common/RelativeTime.vue";
import KnowledgeNav from "@/components/knowledge/KnowledgeNav.vue";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { confirm } from "@/composables/useConfirm";
import { useRouteQuery } from "@/composables/useRouteQuery";
import { useSession } from "@/composables/useSession";
import { t } from "@/copy/strings";
import { errorMessage } from "@/lib/errors";
import { ENTITY_TYPES, entityPath, typeInfo } from "@/lib/knowledge";

definePage({ meta: { title: "Knowledge change log", requires: { capability: "knowledge.read" } } });

const { can } = useSession();
const queryClient = useQueryClient();
const actor = useRouteQuery("actor");
const type = useRouteQuery("type");
const actorDebounced = refDebounced(actor, 300);
const changes = useKnowledgeChanges(() => ({ actor: actorDebounced.value || undefined, type: type.value || undefined }));

const typeOptions = ENTITY_TYPES.map((x) => ({ value: x.type, label: x.plural }));
const KIND_ICON: Record<string, typeof User> = { user: User, human: User, agent: Bot, connector: Plug, system: Plug };
const OP_VARIANT: Record<string, "success" | "info" | "danger" | "muted"> = { create: "success", update: "info", delete: "danger", archive: "danger", restore: "muted" };

async function revert(cs: ChangeSet) {
  const names = cs.entities.map((e) => e.name).join(", ");
  const answer = await confirm({
    title: t.knowledge.revertTitle,
    description: `Undo ${cs.entities.length} change${cs.entities.length === 1 ? "" : "s"} by ${cs.actor} (${names}). Later edits to the same pages are kept where possible.`,
    confirmLabel: t.knowledge.revert,
    destructive: true,
    reason: true,
  });
  if (!answer) return;
  try {
    const res = await revertChangeSet(cs.changeSetId, answer.reason);
    if (res.conflicts.length) toast.warning(`Reverted with ${res.conflicts.length} conflict(s): some pages changed since. Check them.`);
    else toast.success("Reverted.");
    await queryClient.invalidateQueries({ queryKey: ["knowledge"] });
  } catch (err) {
    toast.error(errorMessage(err));
  }
}
</script>

<template>
  <div>
    <PageHeader :title="t.knowledge.changeLog" :description="t.knowledge.changeLogDescription" />
    <KnowledgeNav />
    <FilterBar v-model:search="actor" placeholder="Who (exact username, e.g. anna.k)" search-label="Filter by user" label="Filter changes" class="mb-5">
      <SimpleSelect v-model="type" :options="typeOptions" allow-empty empty-label="All page types" placeholder="All page types" class="w-full sm:w-48" aria-label="Page type" />
    </FilterBar>

    <ListSkeleton v-if="changes.isPending.value" :rows="5" />
    <ErrorState v-else-if="changes.error.value" :error="changes.error.value" @retry="changes.refetch()" />
    <EmptyState v-else-if="!changes.data.value?.length" :icon="Activity" :title="t.knowledge.changeLogEmpty" />
    <ol v-else class="flex flex-col gap-3">
      <li v-for="cs in changes.data.value" :key="cs.changeSetId" class="rounded-lg border bg-card p-4">
        <div class="flex flex-wrap items-center gap-x-3 gap-y-1">
          <span class="flex items-center gap-1.5 text-sm font-medium">
            <component :is="KIND_ICON[cs.actorKind] ?? User" class="size-4 text-muted-foreground" aria-hidden="true" /> {{ cs.actor }}
          </span>
          <span class="text-sm text-muted-foreground">{{ cs.reason || "No reason given" }}</span>
          <span class="ml-auto text-xs text-muted-foreground"><RelativeTime :value="cs.createdAt" /> · via {{ cs.via }}</span>
        </div>
        <ul class="mt-2 flex flex-wrap gap-2">
          <li v-for="e in cs.entities" :key="`${e.type}/${e.slug}`">
            <RouterLink :to="entityPath(e.type, e.slug)" class="inline-flex items-center gap-1.5 rounded-md border px-2 py-1 text-sm hover:border-primary/40">
              <Badge :variant="OP_VARIANT[e.op] ?? 'muted'" class="px-1.5">{{ e.op }}</Badge>
              <component :is="typeInfo(e.type).icon" class="size-3.5" :style="{ color: typeInfo(e.type).color }" aria-hidden="true" /> {{ e.name }}
            </RouterLink>
          </li>
        </ul>
        <div v-if="can('knowledge.write')" class="mt-3 flex justify-end">
          <Button size="sm" variant="ghost" @click="revert(cs)"><Undo2 /> {{ t.knowledge.revert }}</Button>
        </div>
      </li>
    </ol>
  </div>
</template>
